import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import Skeleton from '@mui/material/Skeleton';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import {
  PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES,
  PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableMissionTypeForm,
  ResolvableMissionTypeFormTab,
} from '../../forms/ResolvableMissionType';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

import { useFindAllDocumentTemplatesQuery } from '../DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';

export const ResolvableMissionTypePage_Templates_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_Templates_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      reportTemplateIds
      quotationTemplateId
      ...ResolvableMissionTypeForm_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_Templates_ResolvableMissionType_Fragment
  >;
}

export const Templates = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_Templates_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES,
      edit: PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES_EDIT,
    },
    { id: resolvableMissionType._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { data: allDocumentTemplateQuery } = useFindAllDocumentTemplatesQuery();

  const reportTemplates =
    allDocumentTemplateQuery?.findAllDocumentTemplates.filter((template) =>
      resolvableMissionType.reportTemplateIds?.includes(template._id),
    ) ?? [];

  const quotationTemplate =
    allDocumentTemplateQuery?.findAllDocumentTemplates.find(
      (template) => template._id === resolvableMissionType.quotationTemplateId,
    );

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            Modèles de rapport
          </Typography>
          {!resolvableMissionType.reportTemplateIds?.length ? (
            <Typography gutterBottom variant="body1">
              Aucun modèle de rapport défini
            </Typography>
          ) : !allDocumentTemplateQuery ? (
            <Skeleton />
          ) : (
            reportTemplates.map((template) => (
              <Typography gutterBottom key={template._id} variant="body1">
                {`${template.code} - ${template.label}`}
              </Typography>
            ))
          )}
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            Modèle de devis
          </Typography>
          <Typography gutterBottom variant="body1">
            {!resolvableMissionType.quotationTemplateId ? (
              'Aucun modèle de devis défini'
            ) : !quotationTemplate ? (
              <Skeleton />
            ) : (
              `${quotationTemplate.code} - ${quotationTemplate.label}`
            )}
          </Typography>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editMissionTypeTemplatesParameters',
            icon: <EditIcon />,
            name: 'Modifier Paramétrage Modèles',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur le Paramétrage des Modèles du Type de Mission"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableMissionTypeForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableMissionType}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableMissionTypeId={resolvableMissionType._id}
          stepIdsSubset={[ResolvableMissionTypeFormTab.Templates]}
          title="Formulaire de modification du Paramétrage des Modèles du Type de Mission"
        />
      </RightDrawer>
    </>
  );
};
